import React, { useEffect } from 'react';
import { Route } from 'react-router-dom';
import { apiDocsPlugin, ApiExplorerPage } from '@backstage/plugin-api-docs';
import {
  CatalogEntityPage,
  CatalogIndexPage,
  catalogPlugin,
} from '@backstage/plugin-catalog';
import {
  CatalogImportPage,
  catalogImportPlugin,
} from '@backstage/plugin-catalog-import';
import { ScaffolderPage, scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { orgPlugin } from '@backstage/plugin-org';
import { SearchPage } from '@backstage/plugin-search';
import {
  TechDocsIndexPage,
  techdocsPlugin,
  TechDocsReaderPage,
} from '@backstage/plugin-techdocs';
import { TechDocsAddons } from '@backstage/plugin-techdocs-react';
import { ReportIssue } from '@backstage/plugin-techdocs-module-addons-contrib';
import { UserSettingsPage } from '@backstage/plugin-user-settings';
import { HomepageCompositionRoot } from '@backstage/plugin-home';
import { apis } from './apis';
import { entityPage } from './components/catalog/EntityPage';
import { searchPage } from './components/search/SearchPage';
import { Root } from './components/Root';
import { HomePage } from './components/home/HomePage';

import {
  AlertDisplay,
  OAuthRequestDialog,
  SignInPage,
} from '@backstage/core-components';
import { createApp } from '@backstage/app-defaults';
import { AppRouter, FlatRoutes } from '@backstage/core-app-api';
import { CatalogGraphPage } from '@backstage/plugin-catalog-graph';
import { RequirePermission } from '@backstage/plugin-permission-react';
import { catalogEntityCreatePermission } from '@backstage/plugin-catalog-common/alpha';
import { providers } from './components/signin/identityProviders';
import { TechRadarPage } from '@backstage-community/plugin-tech-radar';
import { RbacPage } from '@backstage-community/plugin-rbac';
import { useApi, identityApiRef } from '@backstage/core-plugin-api';
import { CustomExtensionCatalogPage } from '@internal/backstage-plugin-custom-extension-catalog';
import { AnalyticsPage, PageDetailPage, UserDetailPage, PageAnalyticsFab } from '@internal/backstage-plugin-analytics';

const DatadogRUMWrapper = ({ children }: { children: React.ReactNode }) => {
  const identityApi = useApi(identityApiRef);

  useEffect(() => {
    const setDatadogUser = async () => {
      try {
        const profile = await identityApi.getProfileInfo();
        if (window.DD_RUM && profile?.email) {
          window.DD_RUM.setUser({
            id: profile.email,
            name: profile.displayName || 'Anonymous',
            email: profile.email,
          });
        }
      } catch (err) {
        console.warn('Failed to set Datadog RUM user:', err);
      }
    };

    setDatadogUser();
  }, [identityApi]);

  return <>{children}</>;
};

const app = createApp({
  apis,
  components: {
    SignInPage: props => <SignInPage {...props} auto providers={providers} />,
  },
  bindRoutes({ bind }) {
    bind(catalogPlugin.externalRoutes, {
      createComponent: scaffolderPlugin.routes.root,
      viewTechDoc: techdocsPlugin.routes.docRoot,
      createFromTemplate: scaffolderPlugin.routes.selectedTemplate,
    });
    bind(apiDocsPlugin.externalRoutes, {
      registerApi: catalogImportPlugin.routes.importPage,
    });
    bind(scaffolderPlugin.externalRoutes, {
      registerComponent: catalogImportPlugin.routes.importPage,
      viewTechDoc: techdocsPlugin.routes.docRoot,
    });
    bind(orgPlugin.externalRoutes, {
      catalogIndex: catalogPlugin.routes.catalogIndex,
    });
  },
});

const routes = (
  <FlatRoutes>
    <Route path="/" element={<HomepageCompositionRoot />}>
      <HomePage />
    </Route>
    <Route path="/catalog" element={<CatalogIndexPage />} />
    <Route
      path="/catalog/:namespace/:kind/:name"
      element={<CatalogEntityPage />}
    >
      {entityPage}
    </Route>
    <Route path="/docs" element={<TechDocsIndexPage />} />
    <Route
      path="/docs/:namespace/:kind/:name/*"
      element={<TechDocsReaderPage />}
    >
      <TechDocsAddons>
        <ReportIssue />
      </TechDocsAddons>
    </Route>
    <Route path="/create" element={<ScaffolderPage />} />
    <Route path="/api-docs" element={<ApiExplorerPage />} />
    <Route
      path="/catalog-import"
      element={
        <RequirePermission permission={catalogEntityCreatePermission}>
          <CatalogImportPage />
        </RequirePermission>
      }
    />
    <Route path="/search" element={<SearchPage />}>
      {searchPage}
    </Route>
    <Route path="/settings" element={<UserSettingsPage />} />
    <Route path="/catalog-graph" element={<CatalogGraphPage />} />
    <Route
      path="/tech-radar"
      element={<TechRadarPage width={1500} height={800} />}
    />
    <Route path="/rbac" element={<RbacPage />} />;
    <Route path="/custom-extension-catalog" element={<CustomExtensionCatalogPage />} />
    <Route path="/analytics" element={<AnalyticsPage />} />
    <Route path="/analytics/page/:page" element={<PageDetailPage />} />
    <Route path="/analytics/user/:user" element={<UserDetailPage />} />
  </FlatRoutes>
);

export default app.createRoot(
  <DatadogRUMWrapper>
    <AlertDisplay />
    <OAuthRequestDialog />
    <AppRouter>
      <Root>{routes}</Root>
      <PageAnalyticsFab />
    </AppRouter>
  </DatadogRUMWrapper>,
);
